'use client'

import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { useEffect, useState } from 'react'

const AB_TASTY_POLL_INTERVAL_MS = 1000
// AbTasty convention: the Original/control variation is always id 0, any other id is a modified variation returning the active ABTesting
const AB_TASTY_ORIGINAL_VARIATION_ID = 0

// isAuthenticated is only consulted by the local escape hatch below, it has no bearing on the real window.ABTasty branch (not a generic dependency of this hook, see the escape hatch comment)
function isCampaignAccepted(campaignId: number, isAuthenticated: boolean): boolean {
  // Prefer a real (or mocked, e.g. in Storybook) window.ABTasty whenever one is present
  if (window.ABTasty) {
    const test = window.ABTasty.getTestsOnPage(campaignId)?.[campaignId]

    return test?.status === 'accepted' && test?.variationID !== AB_TASTY_ORIGINAL_VARIATION_ID
  }

  // Local/preview testing escape hatch: the AbTasty tag isn't reachable outside authorized domains (prolians, hydralians and dexis)
  // same condition as ABTasty campaign locally
  return !!(process.env.NEXT_PUBLIC_ABTASTY_CAMPAIGN_ENABLED && isAuthenticated)
}

// Reads whether the given AbTasty campaign currently qualifies the visitor, polled since window.ABTasty re-evaluates targeting live and only exists client-side after the tag runs (never known during SSR)
export function useAbTastyCampaignActive(campaignId: number): boolean {
  const isHydrated = useNextIsClientHydrated()
  const isAuthenticated = useIsAuthenticated()
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    if (!isHydrated) return

    const check = () => setIsActive(isCampaignAccepted(campaignId, isAuthenticated))
    check()
    const interval = setInterval(check, AB_TASTY_POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [isHydrated, campaignId, isAuthenticated])

  return isActive
}
